import React, { useState, useEffect, memo, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../supabase/config';
import { logger } from '../utils/logger';

const ChildSettingsManager = memo(({ child, onUpdate }) => {
  const { user } = useUser();
  const [settings, setSettings] = useState({
    difficulty: 'medium',
    sound_enabled: true,
    music_enabled: true,
    theme: 'light'
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (child?.preferences) {
      setSettings({
        difficulty: child.preferences.difficulty || 'medium',
        sound_enabled: child.preferences.sound_enabled ?? true,
        music_enabled: child.preferences.music_enabled ?? true,
        theme: child.preferences.theme || 'light'
      });
    }
  }, [child]);

  const handleUpdate = useCallback(async (updates) => {
    if (!child?.id || !user?.id) return;

    setIsUpdating(true);
    setError(null);

    try {
      const newPreferences = {
        ...child.preferences,
        ...updates
      };

      const { error: updateError } = await supabase
        .from('users')
        .update({ preferences: newPreferences })
        .eq('id', child.id);

      if (updateError) {
        throw updateError;
      }

      setSettings(prev => ({ ...prev, ...updates }));

      if (onUpdate) {
        onUpdate({ ...child, preferences: newPreferences });
      }

      logger.info('Child settings updated:', { childId: child.id, updates });
    } catch (err) {
      logger.error('Error updating child settings:', err);
      setError(err.message || 'Failed to update settings');
    } finally {
      setIsUpdating(false);
    }
  }, [child, user, onUpdate]);

  if (!child) {
    return (
      <div className="text-center py-8 text-gray-500">
        Select a child to manage their settings
      </div>
    );
  }

  if (child.parent_controls_enabled === false) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <span className="text-yellow-600 mr-2">⚠️</span>
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-1">Parent controls are disabled</p>
            <p>You cannot modify this child's settings. Enable parent controls to manage preferences.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <span>⚙️</span>
        <span>Learning Preferences</span>
      </h3>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Difficulty Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Difficulty Level
          </label>
          <div className="grid grid-cols-3 gap-2">
            {['easy', 'medium', 'hard'].map(level => (
              <button
                key={level}
                onClick={() => handleUpdate({ difficulty: level })}
                disabled={isUpdating}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  settings.difficulty === level
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } disabled:opacity-50`}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Controls the complexity of questions and activities
          </p>
        </div>

        {/* Sound Settings */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sound Effects
          </label>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.sound_enabled}
              onChange={(e) => handleUpdate({ sound_enabled: e.target.checked })}
              disabled={isUpdating}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            <span className="ml-3 text-sm text-gray-700">
              {settings.sound_enabled ? '🔊 Enabled' : '🔇 Disabled'}
            </span>
          </label>
        </div>

        {/* Music Settings */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Background Music
          </label>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.music_enabled}
              onChange={(e) => handleUpdate({ music_enabled: e.target.checked })}
              disabled={isUpdating}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            <span className="ml-3 text-sm text-gray-700">
              {settings.music_enabled ? '🎵 Enabled' : '🔇 Disabled'}
            </span>
          </label>
        </div>

        {/* Theme Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Theme
          </label>
          <div className="grid grid-cols-2 gap-2">
            {['light', 'dark'].map(theme => (
              <button
                key={theme}
                onClick={() => handleUpdate({ theme: theme })}
                disabled={isUpdating}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  settings.theme === theme
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } disabled:opacity-50`}
              >
                {theme === 'light' ? '☀️ Light' : '🌙 Dark'}
              </button>
            ))}
          </div>
        </div>

        {isUpdating && (
          <div className="text-center text-sm text-gray-500">
            Updating settings...
          </div>
        )}
      </div>
    </div>
  );
});

ChildSettingsManager.displayName = 'ChildSettingsManager';

export default ChildSettingsManager;

